import React from 'react';
import PropTypes from 'prop-types';

import styled, { css } from 'styled-components';

const smallButtonStyles = css`
  padding: ${props => props.padding || '0 12px'};
  height: 30px;
  line-height: 30px;
  font-size: 14px;
  letter-spacing: 0.5px;
  border-radius: 5px;
`;

const mediumButtonStyles = css`
  padding: ${props => props.padding || '0 18px'};
  height: 40px;
  line-height: 40px;
  font-size: 17px;
  letter-spacing: 0.6px;
  border-radius: 6px;
`;

const largeButtonStyles = css`
  padding: ${props => props.padding || '0 30px'};
  height: 50px;
  line-height: 50px;
  font-size: 19px;
  border-radius: 8px;
`;

const primaryButtonStyles = css`
  ${props => {
    if (props.size === 'small') return smallButtonStyles;
    if (props.size === 'medium') return mediumButtonStyles;
    if (props.size === 'large') return largeButtonStyles;
    return mediumButtonStyles;
  }};

  background-image: ${props =>
    props.disabled
      ? 'none'
      : `linear-gradient(to bottom, ${props.theme.colors.button}, ${
          props.theme.colors.buttonDark
        })`};
  background-color: ${props =>
    props.disabled
      ? props.theme.elem.button.disabled
      : props.theme.colors.transparent};
  color: ${props => props.theme.colors.white};

  &:hover {
    background-image: none;
    background-color: ${props =>
      props.disabled
        ? props.theme.elem.button.disabled
        : props.theme.colors.buttonDark};
    transition: background-color 0ms ease-in, color 0ms ease-in;
  }

  &:active {
    position: relative;
    top: 1px;
    background-image: none;
    background-color: ${props =>
      props.disabled
        ? props.theme.elem.button.disabled
        : props.theme.colors.buttonDark};
    transition: background-color 0ms ease-in, color 0ms ease-in;
  }
`;

const secondaryButtonStyles = css`
  ${props => {
    if (props.size === 'small') return smallButtonStyles;
    if (props.size === 'medium') return mediumButtonStyles;
    if (props.size === 'large') return largeButtonStyles;

    return mediumButtonStyles;
  }};

  border: 1px solid
    ${props =>
      props.disabled
        ? props.theme.elem.button.disabled
        : props.theme.colors.button};
  background-color: ${props => props.theme.colors.white};
  color: ${props =>
    props.disabled
      ? props.theme.elem.button.disabled
      : props.theme.colors.brand};

  &:hover {
    border: 1px solid
      ${props =>
        props.disabled
          ? props.theme.elem.button.disabled
          : props.theme.colors.buttonDark};
    color: ${props =>
      props.disabled
        ? props.theme.elem.button.disabled
        : props.theme.colors.buttonDark};
  }

  &:active {
    position: relative;
    top: 1px;
    border: 1px solid
      ${props =>
        props.disabled
          ? props.theme.elem.button.disabled
          : props.theme.colors.buttonDark};
    color: ${props =>
      props.disabled
        ? props.theme.elem.button.disabled
        : props.theme.colors.buttonDark};
  }
`;

const textButtonStyles = css`
  padding: 0;
  height: auto;
  border-radius: 0;
  vertical-align: baseline;
  background-color: ${props => props.theme.colors.transparent};
  border-bottom: 1px solid ${props => props.theme.colors.link};
  color: ${props => props.theme.colors.link};
  opacity: ${props =>
    props.disabled
      ? props.theme.opacity.translucent
      : props.theme.opacity.default};
`;

const StyledButton = styled.button`
  display: inline-block;
  box-sizing: border-box;
  flex: ${props => (props.inline ? '1' : 'none')};
  border: 0;
  width: ${props => props.width || 'auto'};
  font-family: ${props => props.theme.fonts[props.font]};
  font-weight: ${props => (props.bold ? 'bold' : 'normal')};
  letter-spacing: ${props => props.spacing || 'normal'};
  transition: background-color 200ms ease-out, color 200ms ease-out;
  cursor: ${props => (props.disabled ? 'default' : 'pointer')};
  outline: none;

  ${props => {
    if (props.type === 'text') return textButtonStyles;
    if (props.type === 'secondary') return secondaryButtonStyles;
    if (props.type === 'primary') return primaryButtonStyles;

    return null;
  }};
`;

class Button extends React.Component {
  static propTypes = {
    name: PropTypes.string,
    onClick: PropTypes.func,
    children: PropTypes.oneOfType([PropTypes.string, PropTypes.element])
      .isRequired,
    size: PropTypes.oneOf(['small', 'medium', 'large']),
    type: PropTypes.oneOf(['text', 'primary', 'secondary']),
    font: PropTypes.oneOf(['regular', 'caption']),
    padding: PropTypes.string,
    visible: PropTypes.bool,
    disabled: PropTypes.bool,
    bold: PropTypes.bool
  };

  static defaultProps = {
    name: null,
    onClick: null,
    size: 'medium',
    type: 'primary',
    font: 'regular',
    padding: '',
    visible: true,
    disabled: false,
    bold: false
  };

  click = event => {
    event.target.blur();

    if (this.props.onClick) {
      this.props.onClick();
    }
  };

  render() {
    const { visible, disabled, children, name, ...restProps } = this.props;
    if (typeof visible !== 'undefined' && !visible) return null;

    return (
      <StyledButton
        {...restProps}
        className={`action-${name}`}
        disabled={disabled}
        onClick={this.click}
      >
        {children}
      </StyledButton>
    );
  }
}

export { Button as default, StyledButton };



// WEBPACK FOOTER //
// ./src/components/units/Button/Button.js